import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Skeleton, Typography } from '@mui/material';
import formatPriceWithLocale from '../utils/formatPriceWithLocale';
import { useConversationStore } from '../../ConversationStoreProvider';
const ProductPrice = ({ price, variants, selectedVariant, variant = 'h6', fontSize, currency: currencyProp, format = 'dollars', locale: localeProp, sx = {}, isLoading, }) => {
    const store = useConversationStore();
    const locale = useMemo(() => localeProp
        || store?.customerContext?.currentPage?.locale
        || store?.customerContext?.locale
        || 'en', [localeProp, store?.customerContext]);
    const currency = useMemo(() => currencyProp
        || store?.customerContext?.currentPage?.currency
        || store?.customerContext?.currency
        || 'USD', [currencyProp, store?.customerContext]);
    const displayPrice = useMemo(() => {
        // Helper function to format price with locale
        const formatPrice = (amount) => formatPriceWithLocale(amount, locale, currency);
        if (!variants || variants.length === 0) {
            if (price) {
                return formatPrice(price);
            }
            return 'Price not available';
        }
        // If we have a selected variant, show its price
        if (selectedVariant && selectedVariant.price) {
            const variantPrice = format === 'cents' ? selectedVariant.price / 100 : selectedVariant.price;
            return formatPrice(variantPrice);
        }
        // Smart logic based on variant selection state:
        // - If NO selectedVariant but multiple variants exist → show price range
        // - If single variant product → show single price
        if (variants.length === 1) {
            // Only one variant total → show its price
            const variantPrice = format === 'cents' ? variants[0].price / 100 : variants[0].price;
            return formatPrice(variantPrice);
        }
        // Multiple variants without complete selection → show range
        if (variants.length > 1) {
            // Calculate min-max range
            const prices = variants
                .map((v) => (format === 'cents' ? v.price / 100 : v.price))
                .filter((p) => p > 0);
            if (prices.length === 0) {
                return 'Price not available';
            }
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            if (minPrice === maxPrice) {
                return formatPrice(minPrice);
            }
            return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
        }
        // Show first available price
        const firstVariant = variants.find((v) => v.price > 0);
        if (firstVariant) {
            const variantPrice = format === 'cents' ? firstVariant.price / 100 : firstVariant.price;
            return formatPrice(variantPrice);
        }
        return 'Price not available';
    }, [price, variants, selectedVariant, currency, format, locale]);
    const priceStyles = {
        fontWeight: 'bold',
        color: 'inherit',
        display: 'block',
        margin: 0,
        lineHeight: 1.15,
        ...(fontSize && { fontSize }),
        ...sx,
    };
    return (_jsx(Typography, { variant: variant, sx: priceStyles, children: (!price && isLoading) ? _jsx(Skeleton, { variant: "text", width: "100%" }) : displayPrice }));
};
ProductPrice.defaultProps = {
    selectedVariant: undefined,
    variant: 'h6',
    fontSize: undefined,
    currency: '$',
    format: 'dollars',
    locale: 'en',
    sx: {},
};
export default ProductPrice;
//# sourceMappingURL=ProductPrice.js.map